import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Queue } from '@shared/util/hooks/useQueue';
import { isPopoverShown } from '@shared/util/popover';
import { Toast } from './Toast';
import { QueuedToast } from './useToastCreator';
import styles from './ToastPresenter.module.css';

export function ToastPresenter({ queue }: { queue: Queue<QueuedToast> }) {
  // Rendered toasts: the queue's items plus removed ones still playing their
  // exit animation, kept in place until the animation finishes.
  const [items, setItems] = useState<QueuedToast[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    promoteToTopLayer(container);
    setItems(rendered => mergeWithExiting(rendered, queue.items));
  }, [queue.items]);

  const handleExited = useCallback((toast: QueuedToast) => {
    setItems(rendered => rendered.filter(item => item !== toast));
  }, []);

  // Portal into <body>: while a non-modal Dialog is open, it makes the app root
  // inert, and inertness is inherited by DOM ancestry even in the top layer. As a
  // direct <body> child the popover container is exempted and toasts stay
  // clickable above dialogs.
  return createPortal(
    <div className={styles.toastPresenter} ref={containerRef} popover="manual">
      <ToastGroup
        position="top"
        toasts={items.filter(isPositionTop)}
        activeToasts={queue.items}
        onExited={handleExited}
      />
      <ToastGroup
        position="bottom"
        toasts={items.filter(isPositionBottom)}
        activeToasts={queue.items}
        onExited={handleExited}
      />
    </div>,
    document.body,
  );
}

function ToastGroup({
  position,
  toasts,
  activeToasts,
  onExited,
}: {
  position: 'top' | 'bottom';
  toasts: QueuedToast[];
  activeToasts: QueuedToast[];
  onExited: (toast: QueuedToast) => void;
}) {
  const active = toasts.filter(toast => activeToasts.includes(toast));
  return (
    <div className={styles.toastGroup} data-position={position}>
      {toasts.map(toast => (
        <ToastRenderer
          key={toast.id}
          toast={toast}
          index={active.indexOf(toast)}
          exiting={!activeToasts.includes(toast)}
          onExited={() => onExited(toast)}
        />
      ))}
    </div>
  );
}

// Keep removed toasts at their previous position among the queue's items so
// they collapse in place instead of reordering.
function mergeWithExiting(rendered: QueuedToast[], next: QueuedToast[]): QueuedToast[] {
  const merged = [...next];
  rendered.forEach((toast, index) => {
    if (!next.includes(toast)) {
      merged.splice(Math.min(index, merged.length), 0, toast);
    }
  });
  return merged;
}

// Hide and re-show the container popover to keep toasts in the top layer.
function promoteToTopLayer(container: HTMLElement) {
  if (isPopoverShown(container)) {
    container.hidePopover();
  }
  container.showPopover();
}

function isPositionTop(toast: QueuedToast) {
  return toast.position === 'top';
}

function isPositionBottom(toast: QueuedToast) {
  return toast.position === 'bottom';
}

function ToastRenderer({
  toast,
  index,
  exiting,
  onExited,
}: {
  toast: QueuedToast;
  index: number;
  exiting: boolean;
  onExited: () => void;
}) {
  const { remove, ...toastProps } = toast;
  return (
    <Toast
      {...toastProps}
      onClose={remove}
      autoClose={toastProps.autoClose && index === 0}
      exiting={exiting}
      onExited={onExited}
    />
  );
}
